import { useState } from 'react';
import { motion } from 'framer-motion';
import { usePublish } from '../../lib/usePublish';
import { useComposer } from '../../components/assistant/useComposer';
import { useAssistantSuggestions } from '../../lib/useAssistantSuggestions';
import { useChecklist } from '../../lib/useChecklist';
import AssistantComposer from '../../components/assistant/AssistantComposer';
import SuggestedActions from '../../components/assistant/SuggestedActions';
import GreetingSection from './GreetingSection';
import ActivityFeed from './ActivityFeed';
import HomeWelcomeBanner from '../../components/home/HomeWelcomeBanner';
import SiteDressingBanner from '../../components/home/SiteDressingBanner';
import {
  DashboardMetricsPanel,
  LastDeploySection,
  LiveSiteLink,
  RecentSubmissionsSection,
} from '../dashboard/DashboardSections';
import { useDashboardData } from '../dashboard/useDashboardData';
import type { AssistantIntent } from '../../lib/assistantIntents';

const BANNER_DISMISSED_KEY = 'pal_checklist_banner_dismissed';
const ONE_DAY_S = 86400;

/**
 * Home AI-first (Fase 1). Misma semántica en mobile y desktop, distinta
 * composición: una sola columna en teléfono; columna de trabajo + rail de
 * acciones/actividad en escritorio (desktop companion).
 */
export default function HomeScreen() {
  const { submissions, stats, visits, siteUrl } = useDashboardData();
  const { last: lastDeploy } = usePublish();
  const { data: checklist, pendingCount } = useChecklist();

  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return localStorage.getItem(BANNER_DISMISSED_KEY) === '1'; } catch { return false; }
  });

  const unread = stats?.unread ?? 0;
  const needsPublish = !lastDeploy || lastDeploy.status !== 'success';

  // Mostrar banner si el onboarding fue hace menos de 24h y hay tareas pendientes.
  const nowS = Math.floor(Date.now() / 1000);
  const isNewlyOnboarded = !!checklist?.onboardedAt && (nowS - checklist.onboardedAt) < ONE_DAY_S;
  const showBanner = isNewlyOnboarded && pendingCount > 0 && !bannerDismissed;

  function dismissBanner() {
    setBannerDismissed(true);
    try { localStorage.setItem(BANNER_DISMISSED_KEY, '1'); } catch { /* ignore */ }
  }

  // Un único controlador de composer, compartido con las acciones sugeridas.
  const controller = useComposer('desktop');
  const { suggestions, recordUse, toggleFavorite } = useAssistantSuggestions('desktop', {
    hasUnreadLeads: unread > 0,
    needsPublish,
  });

  function handleSuggestion(intent: AssistantIntent) {
    recordUse(intent.id);
    controller.runIntent(intent);
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
        {/* Columna de trabajo */}
        <div className="space-y-6 min-w-0">
          <SiteDressingBanner active={isNewlyOnboarded} />
          {showBanner && checklist && (
            <HomeWelcomeBanner
              pendingCount={pendingCount}
              completedCount={checklist.completedCount}
              totalItems={checklist.totalItems}
              onDismiss={dismissBanner}
            />
          )}
          <GreetingSection unread={unread} />
          <AssistantComposer platform="desktop" controller={controller} />

          {/* Acciones rápidas — chips en mobile, ocultas en el rail en desktop */}
          <div className="lg:hidden">
            <SectionLabel>Acciones rápidas</SectionLabel>
            <SuggestedActions
              suggestions={suggestions}
              onSelect={handleSuggestion}
              onToggleFavorite={toggleFavorite}
            />
          </div>

          <div>
            <SectionLabel>Estado del negocio</SectionLabel>
            <DashboardMetricsPanel stats={stats} visits={visits} />
          </div>

          <div className="lg:hidden space-y-6">
            <LiveSiteLink siteUrl={siteUrl} />
            <LastDeploySection lastDeploy={lastDeploy} />
          </div>

          <div>
            <SectionLabel>Actividad reciente</SectionLabel>
            <div className="space-y-4">
              <ActivityFeed />
              <RecentSubmissionsSection submissions={submissions} />
            </div>
          </div>
        </div>

        {/* Rail companion — solo desktop */}
        <motion.aside
          initial={{ opacity: 0, x: 8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.2 }}
          className="hidden lg:block space-y-6"
        >
          <div>
            <SectionLabel>Acciones rápidas</SectionLabel>
            <SuggestedActions
              suggestions={suggestions}
              onSelect={handleSuggestion}
              onToggleFavorite={toggleFavorite}
            />
          </div>
          <LiveSiteLink siteUrl={siteUrl} />
          <LastDeploySection lastDeploy={lastDeploy} />
        </motion.aside>
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-3 text-sm font-medium text-ui-text-muted uppercase tracking-wider">{children}</h2>
  );
}
